import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, Plus, Edit, Trash2, Loader2, Save, Building } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const LocationsManagement = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  
  const [form, setForm] = useState({
    name: '',
    address: '',
    latitude: '',
    longitude: '',
    allowed_radius_meters: 100
  });

  const fetchLocations = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/hr/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocations(response.data.locations || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
      toast.error('Failed to load locations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const handleOpenDialog = (location = null) => {
    if (location) {
      setEditingLocation(location);
      setForm({
        name: location.name || '',
        address: location.address || '',
        latitude: location.latitude || '',
        longitude: location.longitude || '',
        allowed_radius_meters: location.allowed_radius_meters || 100
      });
    } else {
      setEditingLocation(null);
      setForm({
        name: '',
        address: '',
        latitude: '',
        longitude: '',
        allowed_radius_meters: 100
      });
    }
    setDialogOpen(true);
  };

  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported');
      return;
    }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setForm(prev => ({
          ...prev,
          latitude: position.coords.latitude.toFixed(6),
          longitude: position.coords.longitude.toFixed(6)
        }));
        setGettingLocation(false);
        toast.success('Location captured!');
      },
      (error) => {
        toast.error('Failed to get location: ' + error.message);
        setGettingLocation(false);
      },
      { enableHighAccuracy: true }
    );
  };

  const handleSave = async () => {
    if (!form.name || !form.latitude || !form.longitude) {
      toast.error('Name and coordinates are required');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const data = {
        name: form.name,
        address: form.address,
        latitude: parseFloat(form.latitude),
        longitude: parseFloat(form.longitude),
        allowed_radius_meters: parseInt(form.allowed_radius_meters)
      };

      if (editingLocation) {
        await axios.put(
          `${API_BASE_URL}/hr/locations/${editingLocation.id}`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Location updated');
      } else {
        await axios.post(
          `${API_BASE_URL}/hr/locations`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Location created');
      }

      setDialogOpen(false);
      fetchLocations();
    } catch (error) {
      console.error('Error saving location:', error);
      toast.error(error.response?.data?.detail || 'Failed to save location');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (locationId) => {
    if (!window.confirm('Are you sure you want to delete this location?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/hr/locations/${locationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Location deleted');
      fetchLocations();
    } catch (error) {
      console.error('Error deleting location:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete location');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-wt-text-primary">Office Locations</h3>
          <p className="text-sm text-wt-text-muted">Manage warehouse, showroom, and office locations for attendance</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-wt-gold hover:bg-wt-gold-hover text-black"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Location
        </Button>
      </div>

      {locations.length === 0 ? (
        <Card className="bg-wt-bg border-wt-border">
          <CardContent className="py-8 text-center">
            <Building className="h-12 w-12 mx-auto mb-4 text-wt-text-muted" />
            <p className="text-wt-text-muted">No locations configured</p>
            <p className="text-sm text-wt-text-muted mt-1">Add your first office location</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {locations.map((location) => (
            <Card key={location.id} className="bg-wt-bg border-wt-border">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-lg bg-wt-gold/20 flex items-center justify-center">
                      <MapPin className="h-5 w-5 text-wt-gold" />
                    </div>
                    <div>
                      <h4 className="font-medium text-wt-text-primary">{location.name}</h4>
                      {location.address && (
                        <p className="text-sm text-wt-text-muted">{location.address}</p>
                      )}
                      <div className="flex items-center gap-4 mt-1 text-xs text-wt-text-muted">
                        <span>Lat: {location.latitude?.toFixed(4)}</span>
                        <span>Lng: {location.longitude?.toFixed(4)}</span>
                        <span>Radius: {location.allowed_radius_meters}m</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleOpenDialog(location)}
                      className="border-wt-border"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(location.id)}
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-wt-surface border-wt-border">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary">
              {editingLocation ? 'Edit Location' : 'Add Location'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-wt-text-primary">Location Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Main Office, Warehouse 1"
                className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
              />
            </div>

            <div>
              <Label className="text-wt-text-primary">Address</Label>
              <Input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Full address"
                className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-wt-text-primary">Latitude *</Label>
                <Input
                  type="number"
                  step="0.000001"
                  value={form.latitude}
                  onChange={(e) => setForm({ ...form, latitude: e.target.value })}
                  placeholder="25.2048"
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
              <div>
                <Label className="text-wt-text-primary">Longitude *</Label>
                <Input
                  type="number"
                  step="0.000001"
                  value={form.longitude}
                  onChange={(e) => setForm({ ...form, longitude: e.target.value })}
                  placeholder="55.2708"
                  className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
                />
              </div>
            </div>

            <Button
              variant="outline"
              onClick={handleGetCurrentLocation}
              disabled={gettingLocation}
              className="w-full border-wt-border"
            >
              {gettingLocation ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <MapPin className="h-4 w-4 mr-2" />
              )}
              Use Current Location
            </Button>

            <div>
              <Label className="text-wt-text-primary">Allowed Radius (meters)</Label>
              <Input
                type="number"
                value={form.allowed_radius_meters}
                onChange={(e) => setForm({ ...form, allowed_radius_meters: e.target.value })}
                className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
              />
              <p className="text-xs text-wt-text-muted mt-1">
                Employees must be within this distance to mark attendance
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-wt-border">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-wt-gold hover:bg-wt-gold-hover text-black"
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {editingLocation ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LocationsManagement;
